import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import { navigateWithFrom } from '../utils/navigationUtils';
import { useAuth } from '../contexts/AuthContext';
import { fetchTpTechnicians } from '../services/tpTechniciansService';
import { getTechnicianDisplayName } from '../utils/appointmentCategories';
import { sanitizeSearchInput } from '../utils/security';

const extractTechServiceId = (u) => {
  if (!u) return null;
  const ts = u.techService ?? u.techServiceId;
  if (!ts) return null;
  if (typeof ts === 'string' && ts.trim()) return ts.trim();
  if (ts && typeof ts === 'object') {
    const id = ts._id ?? ts.id;
    if (id) return typeof id === 'string' ? id : String(id);
  }
  return null;
};

const TechServiceTechniciansPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useBackRoute('/');
  const { user } = useAuth();
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const techServiceId = useMemo(() => extractTechServiceId(user), [user]);

  const loadTechnicians = useCallback(async () => {
    if (!techServiceId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchTpTechnicians(techServiceId, { aktif: 'all' });
      setTechnicians(res?.veri?.teknisyenler || []);
    } catch (e) {
      setError(e?.message || e?.data?.mesaj || 'Teknisyenler yüklenemedi');
      setTechnicians([]);
    } finally {
      setLoading(false);
    }
  }, [techServiceId]);

  useEffect(() => {
    if (user?.rol === 'tech-service' && techServiceId) loadTechnicians();
  }, [user?.rol, techServiceId, loadTechnicians]);

  const filtered = useMemo(() => {
    const q = sanitizeSearchInput(search)?.toLowerCase() || '';
    if (!q) return technicians.filter((t) => t.aktif !== false);
    return technicians.filter((t) => {
      if (t.aktif === false) return false;
      const name = getTechnicianDisplayName(t).toLowerCase();
      const username = (t.kullaniciAdi || '').toLowerCase();
      const phone = (t.telefon || '').toLowerCase();
      return name.includes(q) || username.includes(q) || phone.includes(q);
    });
  }, [technicians, search]);

  if (user?.rol === 'tech-person') return <Navigate to="/teknisyen" replace />;
  if (user?.rol !== 'tech-service') return <Navigate to="/" replace />;

  const handleOpen = (tech) => {
    const id = tech.id || tech._id;
    if (!id) return;
    navigateWithFrom(navigate, `/tech-service/teknisyenler/${id}`, location, {
      state: { technicianName: getTechnicianDisplayName(tech) },
    });
  };

  return (
    <main className="main-content tech-service-technicians-page">
      <div className="content-header">
        <div>
          <button type="button" className="secondary-btn" onClick={goBack} style={{ marginBottom: '16px' }}>
            <i className="fas fa-arrow-left" />
            <span>Geri Dön</span>
          </button>
          <h1>Teknisyenler</h1>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-box" style={{ maxWidth: '360px' }}>
          <i className="fas fa-search" />
          <input
            type="text"
            placeholder="Ad, kullanıcı adı veya telefon ile ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxLength={100}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <div className="loading-text">Teknisyenler yükleniyor</div>
        </div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : (
        <div className="table-container">
          <table className="admins-table">
            <thead>
              <tr>
                <th>Teknisyen</th>
                <th>Kullanıcı Adı</th>
                <th>Telefon</th>
                <th className="islemler-sutunu">Detay</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-gray)' }}>
                    Teknisyen bulunamadı
                  </td>
                </tr>
              ) : (
                filtered.map((tech) => {
                  const id = tech.id || tech._id;
                  return (
                    <tr
                      key={id}
                      className="tp-technician-row"
                      onClick={() => handleOpen(tech)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{getTechnicianDisplayName(tech)}</td>
                      <td>{tech.kullaniciAdi || '—'}</td>
                      <td>{tech.telefon || '—'}</td>
                      <td className="islemler-sutunu" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          title="Detay"
                          onClick={() => handleOpen(tech)}
                        >
                          <i className="fas fa-chevron-right" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
};

export default TechServiceTechniciansPage;
